// /apps/patient/src/pages/progress/components/ExerciseCompletionCard.tsx
import { Dumbbell, Clock, Activity, Gauge, MessageSquare, TrendingUp, Heart } from 'lucide-react';

export interface ExerciseCompletionData {
  id: string;
  exercise_id: string;
  exercise_name?: string;
  module_name?: string;
  module_type?: string;
  sets_completed?: number;
  reps_completed?: number[];
  weight_used?: (number | string)[];
  time_taken?: number[];
  distance?: number[];
  difficulty_rating?: 'too_easy' | 'just_right' | 'too_hard';
  rpe?: number;
  notes?: string;
  is_cardio?: boolean;
  cardio_data?: {
    total_duration_seconds?: number;
    total_distance?: number;
    distance_unit?: string;
    average_pace?: string;
    average_heart_rate?: number;
  };
}

interface ExerciseCompletionCardProps {
  exercise: ExerciseCompletionData;
}

export function ExerciseCompletionCard({ exercise }: ExerciseCompletionCardProps) {
  const getDifficultyBadge = () => {
    if (!exercise.difficulty_rating) return null;

    const config = {
      'too_easy': { label: 'Too Easy', color: 'bg-green-100 text-green-700' },
      'just_right': { label: 'Just Right', color: 'bg-blue-100 text-blue-700' },
      'too_hard': { label: 'Too Hard', color: 'bg-red-100 text-red-700' }
    };

    const { label, color } = config[exercise.difficulty_rating];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        {label}
      </span>
    );
  };

  const formatSetsReps = () => {
    if (!exercise.sets_completed) return null;

    const parts = [];
    parts.push(`${exercise.sets_completed} sets`);

    if (exercise.reps_completed && exercise.reps_completed.length > 0) {
      const repsDisplay = exercise.reps_completed.every(r => r === exercise.reps_completed![0])
        ? `${exercise.reps_completed[0]} reps`
        : exercise.reps_completed.join(', ') + ' reps';
      parts.push(repsDisplay);
    }

    if (exercise.weight_used && exercise.weight_used.length > 0) {
      const weights = exercise.weight_used.filter(w => w !== null && w !== undefined);
      if (weights.length > 0) {
        const weightDisplay = weights.every(w => w === weights[0])
          ? `@ ${weights[0]} lbs`
          : `@ ${weights.join(', ')} lbs`;
        parts.push(weightDisplay);
      }
    }

    return parts.join(' x ');
  };

  const formatCardioData = () => {
    if (!exercise.cardio_data) return null;

    const parts = [];

    if (exercise.cardio_data.total_duration_seconds) {
      const minutes = Math.floor(exercise.cardio_data.total_duration_seconds / 60);
      const seconds = exercise.cardio_data.total_duration_seconds % 60;
      parts.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    }

    if (exercise.cardio_data.total_distance) {
      parts.push(`${exercise.cardio_data.total_distance} ${exercise.cardio_data.distance_unit || 'mi'}`);
    }

    if (exercise.cardio_data.average_pace) {
      parts.push(`@ ${exercise.cardio_data.average_pace}`);
    }

    return parts.join(' - ');
  };

  const formatTime = () => {
    if (!exercise.time_taken || exercise.time_taken.length === 0) return null;
    const totalSeconds = exercise.time_taken.reduce((a, b) => a + b, 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
  };

  const Icon = exercise.is_cardio ? Activity : Dumbbell;
  const iconBg = exercise.is_cardio ? 'bg-blue-100' : 'bg-gray-100';
  const iconColor = exercise.is_cardio ? 'text-blue-600' : 'text-gray-600';

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-start space-x-3">
        <div className={`p-2 ${iconBg} rounded-lg flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h4 className="font-medium text-gray-900">
                {exercise.exercise_name || 'Unknown Exercise'}
              </h4>
              {exercise.module_name && (
                <p className="text-sm text-gray-500">{exercise.module_name}</p>
              )}
            </div>
            {getDifficultyBadge()}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mt-3">
            {/* Sets/Reps/Weight for strength exercises */}
            {formatSetsReps() && (
              <div className="flex items-center text-sm text-gray-600">
                <Dumbbell className="h-4 w-4 mr-2 text-gray-400" />
                {formatSetsReps()}
              </div>
            )}

            {/* Cardio data */}
            {formatCardioData() && (
              <div className="flex items-center text-sm text-gray-600">
                <TrendingUp className="h-4 w-4 mr-2 text-gray-400" />
                {formatCardioData()}
              </div>
            )}

            {/* Duration/Time */}
            {formatTime() && (
              <div className="flex items-center text-sm text-gray-600">
                <Clock className="h-4 w-4 mr-2 text-gray-400" />
                {formatTime()}
              </div>
            )}

            {/* Heart Rate */}
            {exercise.cardio_data?.average_heart_rate && (
              <div className="flex items-center text-sm text-gray-600">
                <Heart className="h-4 w-4 mr-2 text-red-400" />
                {exercise.cardio_data.average_heart_rate} bpm avg
              </div>
            )}

            {/* RPE */}
            {exercise.rpe && (
              <div className="flex items-center text-sm text-gray-600">
                <Gauge className="h-4 w-4 mr-2 text-gray-400" />
                RPE: {exercise.rpe}/10
              </div>
            )}

            {/* Distance */}
            {exercise.distance && exercise.distance.length > 0 && !exercise.is_cardio && (
              <div className="flex items-center text-sm text-gray-600">
                <Activity className="h-4 w-4 mr-2 text-gray-400" />
                {exercise.distance.reduce((a, b) => a + b, 0)} total distance
              </div>
            )}
          </div>

          {/* Notes */}
          {exercise.notes && (
            <div className="mt-3 flex items-start text-sm text-gray-600 bg-gray-50 rounded-lg p-2">
              <MessageSquare className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
              <span>{exercise.notes}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
